import { Dispatch, SetStateAction, useCallback } from "react";
import {
  AssessmentGroupListFragmentFragment,
  AssessmentTemplateComplianceRequirement,
  AssessmentType,
  OrganizationTalentIndicatorFragment,
} from "types/graphql-schema";

import { ComboboxGenericOption } from "@components/combobox/generic-combobox";

import EngagementSurveyStep from "../steps/engagement-survey-step";
import PeerAssessmentStep from "../steps/peer-assessment-step";
import SelfManagerAssessmentStep from "../steps/self-manager-assessment-step";
import UpwardAssessmentStep from "../steps/upward-assessment-step";
import {
  AssessmentTemplateExtraSettings,
  ComplianceProgramStep,
  ProgramForm,
} from "../types";

const assessmentTypeToProgramStep: {
  [key in AssessmentType]: ComplianceProgramStep;
} = {
  [AssessmentType.Performance]: ComplianceProgramStep.SELF_MANAGER_REVIEW,
  [AssessmentType.Manager]: ComplianceProgramStep.UPWARD_REVIEW,
  [AssessmentType.Peer]: ComplianceProgramStep.PEER_REVIEW,
  [AssessmentType.EngagementSurvey]: ComplianceProgramStep.ENGAGEMENT_SURVEY,
};

const ProgramFormQuestions = ({
  proposedComplianceProgram,
  onUpdateProposedComplianceProgram,
  canUpdate,
  complianceProgramSteps,
  talentIndicatorData,
  onUpdateComplianceProgramSteps,
  onUpdateAssessmentTemplateSettings,
  onUpdateAssessmentTemplate,
  availableAssessmentGroups,
  onAssessmentGroupCreated,
  onAssessmentGroupArchived,
}: {
  canUpdate: boolean;
  proposedComplianceProgram: ProgramForm;
  complianceProgramSteps: ComplianceProgramStep[];
  talentIndicatorData?: OrganizationTalentIndicatorFragment[];
  availableAssessmentGroups: AssessmentGroupListFragmentFragment[];
  onUpdateProposedComplianceProgram: Dispatch<SetStateAction<ProgramForm>>;
  onAssessmentGroupCreated: (
    newAssessmentGroup: AssessmentGroupListFragmentFragment
  ) => void;
  onAssessmentGroupArchived: (
    assessmentType: AssessmentType,
    assessmentGroupId: number
  ) => void;
  onUpdateComplianceProgramSteps: (steps: ComplianceProgramStep[]) => void;
  onUpdateAssessmentTemplateSettings: (
    assessmentType: AssessmentType
  ) => (settings: AssessmentTemplateExtraSettings) => void;
  onUpdateAssessmentTemplate: (
    assessmentType: AssessmentType
  ) => (opt: ComboboxGenericOption<number> | null) => void;
}) => {
  const handleRemoveAssessmentTemplate = useCallback(
    (assessmentType: AssessmentType) => {
      const existingTemplates =
        proposedComplianceProgram.assessmentTemplates as
          | AssessmentTemplateComplianceRequirement[]
          | undefined;
      if (existingTemplates) {
        onUpdateProposedComplianceProgram({
          ...proposedComplianceProgram,
          assessmentTemplates: existingTemplates.filter(
            (g) => g.assessmentType !== assessmentType
          ),
        });
      }
      onUpdateComplianceProgramSteps(
        complianceProgramSteps.filter(
          (s) => s !== assessmentTypeToProgramStep[assessmentType]
        )
      );
    },
    [
      complianceProgramSteps,
      onUpdateComplianceProgramSteps,
      onUpdateProposedComplianceProgram,
      proposedComplianceProgram,
    ]
  );

  return (
    <div className="flex flex-col gap-8">
      {complianceProgramSteps.includes(
        ComplianceProgramStep.SELF_MANAGER_REVIEW
      ) && (
        <SelfManagerAssessmentStep
          proposedComplianceProgram={proposedComplianceProgram}
          canUpdate={canUpdate}
          hideSettings
          complianceProgramSteps={complianceProgramSteps}
          talentIndicatorData={talentIndicatorData}
          onUpdateComplianceProgramSteps={onUpdateComplianceProgramSteps}
          onUpdateAssessmentTemplateSettings={
            onUpdateAssessmentTemplateSettings
          }
          onUpdateAssessmentTemplate={onUpdateAssessmentTemplate}
          availableAssessmentGroups={availableAssessmentGroups}
          onRemoveAssessmentTemplate={handleRemoveAssessmentTemplate}
          onAssessmentGroupCreated={onAssessmentGroupCreated}
          onAssessmentGroupArchived={onAssessmentGroupArchived}
          onUpdateProposedComplianceProgram={onUpdateProposedComplianceProgram}
        />
      )}

      {complianceProgramSteps.includes(ComplianceProgramStep.UPWARD_REVIEW) && (
        <UpwardAssessmentStep
          proposedComplianceProgram={proposedComplianceProgram}
          canUpdate={canUpdate}
          hideSettings
          complianceProgramSteps={complianceProgramSteps}
          talentIndicatorData={talentIndicatorData}
          onUpdateComplianceProgramSteps={onUpdateComplianceProgramSteps}
          onUpdateAssessmentTemplateSettings={
            onUpdateAssessmentTemplateSettings
          }
          onUpdateAssessmentTemplate={onUpdateAssessmentTemplate}
          availableAssessmentGroups={availableAssessmentGroups}
          onRemoveAssessmentTemplate={handleRemoveAssessmentTemplate}
          onAssessmentGroupCreated={onAssessmentGroupCreated}
          onAssessmentGroupArchived={onAssessmentGroupArchived}
        />
      )}

      {complianceProgramSteps.includes(ComplianceProgramStep.PEER_REVIEW) && (
        <PeerAssessmentStep
          proposedComplianceProgram={proposedComplianceProgram}
          canUpdate={canUpdate}
          hideSettings
          complianceProgramSteps={complianceProgramSteps}
          onUpdateComplianceProgramSteps={onUpdateComplianceProgramSteps}
          onUpdateAssessmentTemplateSettings={
            onUpdateAssessmentTemplateSettings
          }
          onUpdateAssessmentTemplate={onUpdateAssessmentTemplate}
          availableAssessmentGroups={availableAssessmentGroups}
          onRemoveAssessmentTemplate={handleRemoveAssessmentTemplate}
          onAssessmentGroupCreated={onAssessmentGroupCreated}
          onAssessmentGroupArchived={onAssessmentGroupArchived}
        />
      )}

      {complianceProgramSteps.includes(
        ComplianceProgramStep.ENGAGEMENT_SURVEY
      ) && (
        <EngagementSurveyStep
          hideSettings
          proposedComplianceProgram={proposedComplianceProgram}
          canUpdate={canUpdate}
          complianceProgramSteps={complianceProgramSteps}
          onUpdateComplianceProgramSteps={onUpdateComplianceProgramSteps}
          onUpdateAssessmentTemplateSettings={
            onUpdateAssessmentTemplateSettings
          }
          onUpdateAssessmentTemplate={onUpdateAssessmentTemplate}
          availableAssessmentGroups={availableAssessmentGroups}
          onRemoveAssessmentTemplate={handleRemoveAssessmentTemplate}
          onAssessmentGroupCreated={onAssessmentGroupCreated}
          onAssessmentGroupArchived={onAssessmentGroupArchived}
        />
      )}
    </div>
  );
};

export default ProgramFormQuestions;
